import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Slider,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopyOutlined";
import { useState } from "react";
import type { PairingCode } from "@shared/types/foyer";
import { foyerApi } from "../http/foyer";
import { useAuth } from "../auth/AuthContext";

interface Props {
  open: boolean;
  premiseId: string;
  premiseName: string;
  onClose: () => void;
  onCreated?: (code: PairingCode) => void;
}

export default function PairTVDialog({
  open,
  premiseId,
  premiseName,
  onClose,
  onCreated,
}: Props) {
  const { user } = useAuth();
  const [days, setDays] = useState(1);
  const [issued, setIssued] = useState<PairingCode | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function reset() {
    setIssued(null);
    setDays(1);
    setErr(null);
    setBusy(false);
  }

  async function generate() {
    setBusy(true);
    setErr(null);
    try {
      const c = await foyerApi.createPairing({
        premiseId,
        expiryDays: days,
        // Stamp the code with the logged-in user - no separate input.
        createdBy: user?.user ?? "admin",
      });
      setIssued(c);
      onCreated?.(c);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  function copy() {
    if (issued) navigator.clipboard.writeText(issued.code).catch(() => {});
  }

  return (
    <Dialog
      open={open}
      onClose={() => {
        onClose();
        reset();
      }}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>New installation code</DialogTitle>
      <DialogContent>
        {!issued ? (
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Generate a pairing code for <strong>{premiseName}</strong>. The
              installer enters it on the TV at first boot. The code will be
              stamped with your account ({user?.user ?? "unknown user"}).
            </Typography>
            <Box>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="baseline"
              >
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Valid for
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {days} {days === 1 ? "day" : "days"}
                </Typography>
              </Stack>
              <Slider
                value={days}
                min={1}
                max={14}
                step={1}
                marks
                onChange={(_, v) => setDays(v as number)}
                data-testid="pairing-days"
              />
            </Box>
            {err && (
              <Typography variant="body2" color="error">
                {err}
              </Typography>
            )}
          </Stack>
        ) : (
          <Stack spacing={2} sx={{ pt: 1, alignItems: "center" }}>
            <Typography variant="body2" color="text.secondary">
              Enter this PIN on the TV to bind it to {premiseName}.
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              data-testid="pairing-code-display"
            >
              <Typography
                variant="h2"
                sx={{
                  fontFamily:
                    'ui-monospace, "SF Mono", Menlo, Consolas, monospace',
                  fontWeight: 600,
                  letterSpacing: "0.15em",
                }}
              >
                {issued.code}
              </Typography>
              <Tooltip title="Copy">
                <IconButton
                  onClick={copy}
                  aria-label="Copy pairing code"
                  data-testid="pairing-copy"
                >
                  <ContentCopyIcon />
                </IconButton>
              </Tooltip>
            </Stack>
            <Stack
              direction="row"
              spacing={3}
              sx={{ color: "text.secondary", mt: 1 }}
            >
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="caption" sx={{ display: "block" }}>
                  Created by
                </Typography>
                <Typography variant="body2">
                  {issued.createdBy ?? "admin"}
                </Typography>
              </Box>
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="caption" sx={{ display: "block" }}>
                  Expires
                </Typography>
                <Typography variant="body2">
                  {new Date(issued.expiresAt).toLocaleString()}
                </Typography>
              </Box>
            </Stack>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            onClose();
            reset();
          }}
        >
          {issued ? "Done" : "Cancel"}
        </Button>
        {!issued && (
          <Button
            variant="contained"
            onClick={generate}
            disabled={busy}
            data-testid="pairing-generate"
          >
            {busy ? "Generating…" : "Generate code"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
